import { useCallback } from "react";
import { useAppShell } from "@salestrak/ui";
import { useHotkey } from "./use-hotkey";
import { AnalyticsContext, NavigationEvent, trackEvent } from "#app/lib/analytics";

const trackToggleSidebar = (open: boolean, via: "rail" | "pane_header" | "hotkey") =>
  trackEvent({
    context: AnalyticsContext.Navigation,
    event: NavigationEvent.ToggleSidebar,
    data: { open, via },
  });

/**
 * True when the module-menu pane is NOT presented beside the rail: the full
 * tier's rail-only collapsed state, or the medium tier with its flyout
 * dismissed. On compact, the drawer being dismissed is not "collapsed" — the
 * whole navigation is simply off-screen — so this always returns false there.
 */
export function useSidebarCollapsed(): boolean {
  const { tier, state, openOverlay } = useAppShell();
  if (tier === "compact") return false;
  if (tier === "medium") return !openOverlay;
  return state === "collapsed";
}

/**
 * Presents the pane again: re-opens the full tier's pinned pane, or summons
 * the medium tier's flyout. Paired with `useSidebarCollapsed` — the module
 * rail renders an expand control only while that reads true, and this is what
 * the control calls.
 *
 * Both hooks exist so shell state reaches components through ONE app-side
 * seam rather than every caller reaching into `useAppShell()` itself.
 */
export function useExpandSidebar(): () => void {
  const { tier, setOpen, setOpenOverlay } = useAppShell();
  return useCallback(() => {
    trackToggleSidebar(true, "rail");
    if (tier === "medium") setOpenOverlay(true);
    else setOpen(true);
  }, [tier, setOpen, setOpenOverlay]);
}

/**
 * Dismisses the pane: collapses the full tier's pinned pane to the rail, or
 * drops the medium tier's flyout. The module-menu header's chevrons-left
 * control calls this; the rail's expand control is its inverse.
 */
export function useCollapseSidebar(): () => void {
  const { tier, setOpen, setOpenOverlay } = useAppShell();
  return useCallback(() => {
    trackToggleSidebar(false, "pane_header");
    if (tier === "medium") setOpenOverlay(false);
    else setOpen(false);
  }, [tier, setOpen, setOpenOverlay]);
}

/**
 * Registers the global bare-`[` shortcut that toggles the pane — expanded vs
 * collapsed on the full tier, flyout presented vs dismissed on medium — the
 * keyboard twin of the pane header's chevrons-left control (whose tooltip
 * advertises it). Inert on compact, where the pane is a drawer and
 * "collapsed" does not exist.
 */
export function useSidebarToggleHotkey(): void {
  const { tier, state, setOpen, openOverlay, setOpenOverlay } = useAppShell();

  // The bare `[` on the physical `BracketLeft` key: ⌘[ is the browser's Back
  // shortcut, and any other modifier chord belongs to someone else.
  useHotkey({
    code: "BracketLeft",
    enabled: tier !== "compact",
    allowInModal: true,
    handler: () => {
      trackToggleSidebar(tier === "medium" ? !openOverlay : state === "collapsed", "hotkey");
      if (tier === "medium") {
        setOpenOverlay(!openOverlay);
      } else {
        setOpen(state === "collapsed");
      }
    },
  });
}
